<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Numbering',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
      default: null,
    },
    objectType: {
      type: String,
      required: false,
      default: null,
    },
    createParams: {
      type: Object,
      required: false,
      default: null,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const resourceTypes = ref([
    'account',
    'contract',
    'dunning_letter',
    'fixed_asset',
    'invoice:out',
    'invoice:in',
    'part',
    'product',
    'rental',
    'rental_contract',
    'worksheet',
  ]);
  const item = ref(null);
  const formId = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_3083451129821185_paging = computed(() => false);
  const b_3083451129821185_columns = computed(() => [
    {
      name: 'id',
      header: $fn.value.pgettext('Numbering', 'ID'),
      width: '120px',
      required: true,
    },
    {
      name: 'name',
      header: $fn.value.pgettext('Numbering', 'Name'),
      width: '120px',
      required: true,
    },
    {
      name: 'data_type',
      header: $fn.value.pgettext('Numbering', 'Data type'),
      width: '120px',
      required: true,
    },
    {
      name: 'value',
      header: $fn.value.pgettext('Numbering', 'Value'),
      width: '120px',
      required: true,
    },
  ]);

  const $data = computed(() => ({
    resourceTypes: resourceTypes.value,
    item: item.value,
    formId: formId.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3083451129821185_paging: b_3083451129821185_paging.value,
    b_3083451129821185_columns: b_3083451129821185_columns.value,
  }));

  const $$load_item = () => {
    return props.oid
      ? $fn.value.fetch('read_numbering', {
          parameters: { id: props.oid },
          asResource: true,
        })
      : $fn.value.schemaDefaults(
          'CreateNumbering',
          _.extend(
            {
              resource_type: props.objectType,
              start: 1,
              increment: 1,
              variables: [],
            },
            props.createParams
          )
        );
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resourceTypes' },
      { name: 'item' },
      { name: 'formId' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3083451129821185_paging', nodeId: '3083451129821185' },
      { name: 'b_3083451129821185_columns', nodeId: '3083451129821185' },
    ],
    loaders: { item: $$load_item, formId: $$load_formId },
    ux: {},
    dataRefs: {
      resourceTypes,
      item,
      formId,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3083451129821185_paging,
      b_3083451129821185_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title:
        props.oid == null
          ? $fn.value.gettext('New Numbering')
          : $fn.value.gettext('Edit Numbering'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.objectType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('objectType');
    }
  );
  watch(
    () => props.createParams,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('createParams');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_o8sz4qlif_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_cmm5afwok_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_hdujsaixx_getActionArgs = async ($event) => {
    return {
      objectType: 'numbering',
      op: 'upsert',
      data: item.value,
    };
  };
  const act_2434514588044777_closeModal_7e0f11246_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
    };
  };

  const act_7315092382398562_closeModal_o8sz4qlif =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_o8sz4qlif_getActionArgs,
    });
  const inst_evh_7315092382398562_close_kfynrlzs8 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_o8sz4qlif,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_kfynrlzs8],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_c7q1l8lgz =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_th4yvp1jy =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_c7q1l8lgz,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_th4yvp1jy],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_1l6ff1dfg =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_fe4dk3fw5 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_1l6ff1dfg,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_fe4dk3fw5],
    event: 'close',
  });
  const act_1040167445267876_closeModal_cmm5afwok =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_cmm5afwok_getActionArgs,
    });
  const inst_evh_1040167445267876_success_yq6woe23i =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_cmm5afwok,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_hdujsaixx = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_hdujsaixx_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_yq6woe23i],
  });
  const inst_evh_1040167445267876_submit_n0k2vpiec =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_hdujsaixx,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_n0k2vpiec],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_7e0f11246 =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_7e0f11246_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_lht1c702p =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_7e0f11246,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_lht1c702p],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_kfynrlzs8,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_th4yvp1jy,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_fe4dk3fw5,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_yq6woe23i,
    inst_evh_1040167445267876_submit_n0k2vpiec,
    submit: inst_evh_1040167445267876_submit_n0k2vpiec,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_lht1c702p,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_kfynrlzs8 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_kfynrlzs8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_th4yvp1jy = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_th4yvp1jy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_fe4dk3fw5 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_fe4dk3fw5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_yq6woe23i = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_yq6woe23i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_n0k2vpiec = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_n0k2vpiec.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_lht1c702p = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_lht1c702p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Numbering"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :resourceTitle="pgettext('Numbering|ResourceTitle', 'Numbering')"
        :resourceKey="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="numbering"
        :readonly="item.locked"
        :labelAlign="$config.forms.defaultLabelAlign"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpRow>
          <ItpCol>
            <ItpFormGroup
              :label-cols-md="2"
              :content-cols-md="5"
              name="resource_type"
              :label="pgettext('Numbering', 'Resource type')"
              labelClass="text-nowrap"
              required
            >
              <ItpFormNativeSelect
                name="resource_type"
                v-model="item.resource_type"
                :options="resourceTypes"
                required
                :placeholder="
                  pgettext('Numbering', 'The type of the resource.')
                "
              >
              </ItpFormNativeSelect>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="2"
              :content-cols-md="5"
              name="key"
              :label="pgettext('Numbering', 'Key')"
              required
              v-if="oid"
            >
              <ItpFormInput
                name="key"
                v-model="item.key"
                required
                :placeholder="
                  pgettext('Numbering', 'The unique key of the sequence.')
                "
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              name="description"
              :label="pgettext('Numbering', 'Description')"
              :label-cols-md="2"
              :content-cols-md="8"
              required
            >
              <ItpFormInput
                name="description"
                v-model="item.description"
                required
                autofocus
                :placeholder="pgettext('Numbering', 'Short name/description.')"
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              name="format"
              :label="pgettext('Numbering', 'Format')"
              :label-cols-md="2"
              :content-cols-md="8"
              required
            >
              <ItpFormInput name="format" v-model="item.format" required>
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              name="start"
              :label="pgettext('Numbering', 'Start')"
              :label-cols-md="2"
              :content-cols-md="2"
              required
            >
              <ItpFormInput
                type="number"
                name="start"
                v-model="item.start"
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              name="increment"
              :label="pgettext('Numbering', 'Increment')"
              :label-cols-md="2"
              :content-cols-md="2"
              required
            >
              <ItpFormInput
                type="number"
                name="increment"
                v-model="item.increment"
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
          </ItpCol>
        </ItpRow>
      </ItpForm>
      <hr />
      <ItpDataTable
        :paging="b_3083451129821185_paging"
        :value="item.variables"
        :columns="b_3083451129821185_columns"
        v-if="loaded"
      >
      </ItpDataTable>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.numbering && forms.numbering.id"
          :text="pgettext('Numbering', 'Save')"
          type="submit"
          variant="primary"
          default
          :disabled="!forms.numbering || !forms.numbering.submittable"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Numbering', 'Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
